import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router'

// Models
import Payment from 'models/Payment'

// Const
import { API_PAYMENT_LIST_BY_CONTRACT } from 'constants/endpoints'

// Api
import { downloadSelectedPayments } from 'api/payment'

// Services
import message from 'services/message'

// Helpers
import { resolveErrorMessage, resolveRouteUrl } from 'helpers'

// Use
import useTable from 'use/table'

// Paths
import { PATH_PAYMENT_VIEW } from 'routes/paths'

// Components
import Box from 'ui/Box'
import Text from 'ui/Text'
import Heading from 'ui/Heading'
import Datatable from 'ui/Datatable'
import PaymentTrackerFilters, { defaultFilters } from './PaymentTrackerFilters'
import ColumnAction from './Columns/ColumnAction'
import ColumnAmount from './Columns/ColumnAmount'
import ColumnAmountMobile from './Columns/ColumnAmountMobile'
import ColumnDateUploadedOR from './Columns/ColumnDateUploadedOR'
import ColumnPostingDate from './Columns/ColumnPostingDate'
import ColumnPostingDateMobile from './Columns/ColumnPostingDateMobile'
import ColumnPaymentReceived from './Columns/ColumnPaymentReceived'
import ColumnStatus from './Columns/ColumnStatus'

function PaymentTracker({
  contractNumber,
  vendorCode,
  companyCode,
  lessorId,
  vendorId,
  companyId,
}) {
  const history = useHistory()
  const [selectedType, setSelectedType] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])
  const [filters, setFilters] = useState(defaultFilters)

  const params = useMemo(() => {
    return { ...filters, contractNumber, vendorCode, companyCode }
  }, [companyCode, contractNumber, filters, vendorCode])

  const {
    loading,
    data,
    total,
    page,
    onSortChange,
    onPageChange,
    setLoading,
    sortBy,
    sortDirection,
  } = useTable({
    url: API_PAYMENT_LIST_BY_CONTRACT,
    method: 'POST',
    filters: params,
    model: Payment,
  })

  const rowClick = (row) => {
    return {
      onClick: () => {
        const url = resolveRouteUrl({
          path: PATH_PAYMENT_VIEW,
          params: {
            paymentId: row.id,
            paymentMethod: row.paymentMethod,
            contractNumber,
            vendorCode,
            companyCode,
          },
          query: { lessorId, vendorId, companyId },
        })

        history.push(url)
      },
    }
  }

  const columns = [
    {
      title: 'Posting date',
      dataIndex: 'postingDate',
      noWrap: true,
      sorter: true,
      onCell(row) {
        return rowClick(row)
      },
      render(text, row) {
        return <ColumnPostingDate row={row} />
      },
      renderMobile(text, row) {
        return <ColumnPostingDateMobile row={row} />
      },
    },
    {
      title: 'Amount paid',
      dataIndex: 'amountPaid',
      noWrap: true,
      onCell(row) {
        return rowClick(row)
      },
      render(text, row) {
        return <ColumnAmount row={row} />
      },
      renderMobile(text, row) {
        return <ColumnAmountMobile row={row} />
      },
    },
    {
      title: 'Payment method',
      dataIndex: 'method',
      width: '30%',
      onCell(row) {
        return rowClick(row)
      },
    },
    {
      title: 'Payment status',
      dataIndex: 'status',
      sorter: true,
      width: '30%',
      onCell(row) {
        return rowClick(row)
      },
      render(text, row) {
        return <ColumnStatus row={row} />
      },
    },
    {
      title: 'Payment received',
      dataIndex: 'dateReceived',
      width: '8%',
      noWrap: true,
      onCell(row) {
        return rowClick(row)
      },
      render(text, row) {
        return <ColumnPaymentReceived row={row} />
      },
    },
    {
      title: 'O.R uploaded',
      dataIndex: 'dateUploadedOR',
      width: '8%',
      noWrap: true,
      onCell(row) {
        return rowClick(row)
      },
      render(text, row) {
        return <ColumnDateUploadedOR row={row} />
      },
    },
    {
      dataIndex: 'action',
      title: 'Action',
      width: '8%',
      render(text, row) {
        return (
          <ColumnAction
            row={row}
            vendorCode={vendorCode}
            companyCode={companyCode}
            contractNumber={contractNumber}
            downloadDisabled={showDownloadButton}
            setLoading={setLoading}
          />
        )
      },
    },
  ]

  const rowHighlight = (record) => {
    return !record.isAcknowledged
  }

  const selectTypeChange = (type) => {
    setSelectedType(type)
  }

  const selectChange = (rows) => {
    setSelectedRows(rows)
  }

  const onFiltersChange = (filters) => {
    setFilters(filters)
  }

  const onDownloadClick = async (typeFile) => {
    setLoading(true)
    try {
      await downloadSelectedPayments({
        filters: {
          ...params,
          mode: selectedType,
          selected: selectedRows.map((x) => x.id),
        },
        typeFile,
        sortBy,
        sortDirection,
      })
    } catch (error) {
      message.error(await resolveErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const showDownloadButton = useMemo(() => {
    if (data.length) {
      if (selectedType == 'all') {
        return true
      }

      if (selectedType == 'only' && selectedRows.length > 0) {
        return true
      }

      if (selectedType == 'exclude' && selectedRows.length > 0) {
        return true
      }
    }

    return false
  }, [data, selectedType, selectedRows.length])

  return (
    <Box>
      <Heading size={18} paddingTop={15} paddingBottom={0}>
        Payment record
      </Heading>
      <Text color={'#1A1A1A'}>
        Please make sure to do the following when applicable:
      </Text>
      <Text color={'#1A1A1A'}>• Mark as payment received</Text>
      <Text color={'#1A1A1A'} paddingBottom={15}>
        • Upload official receipt
      </Text>
      <Datatable
        rowHighlight={rowHighlight}
        loading={loading}
        page={page}
        columns={columns}
        data={data}
        total={total}
        responsive={true}
        perPage={10}
        selectable={true}
        selected={selectedRows}
        selectMode={selectedType}
        onSelectModeChange={selectTypeChange}
        onSelectedChange={selectChange}
        onPageChange={onPageChange}
        onSortChange={onSortChange}
      >
        <PaymentTrackerFilters
          filters={filters}
          loading={loading}
          onFiltersChange={onFiltersChange}
          onDownloadClick={onDownloadClick}
          showDownloadButton={showDownloadButton}
        />
      </Datatable>
    </Box>
  )
}

PaymentTracker.propTypes = {
  contractNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vendorCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lessorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

PaymentTracker.defaultProps = {
  contractNumber: '',
  vendorCode: '',
  companyCode: '',
  lessorId: '',
  vendorId: '',
  companyId: '',
}

export default PaymentTracker
